import React, { useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Container,
  Divider,
  Chip,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Link as RouterLink } from 'react-router-dom';
import ResponsiveLayout from '../components/Layout/ResponsiveLayout';
import { getQuickNoteCaptureState } from '../utils/quickLogRoutingDecision';
import {
  buildCanonicalQuickNoteSavePayload,
  buildCanonicalStructuredQuickNoteSaveEntry,
} from '../utils/quickLogSavePayload';
import {
  extractTextFromImportFile,
  parseImportedFileText,
} from '../components/Dashboard/ImportLogs';
import { parseNaturalCaptureText } from '../components/Dashboard/ImportLogs/importLogsParsing';
import { buildPreservedImportDocData } from '../components/Dashboard/ImportLogs/importLogsDocBuilders';
import { isDebugFlagEnabled } from '../utils/debugTools';

const QUICK_SIMPLE_SAMPLE = `Had a calm morning. She was a little tired at first but warmed up after breakfast.`;
const QUICK_STRUCTURED_SAMPLE = `Breakfast was waffles and yogurt.
Around 1:10 PM she had a hard moment during a transition and needed support to settle.`;
const PASTED_EMAIL_SAMPLE = `Hi,

Here are today's updates.

This morning she woke up around 6:45 AM and seemed tired. After breakfast she was more settled. Later in the afternoon, she had a tough moment after plans changed. It took about an hour to calm down.

Thanks,`;
const IMPORTED_FILE_SAMPLE = `March 2024
Started Guanfacine at night.

April 2024
Sleep has honestly been exhausting. She keeps waking up around 4 AM.

April 30
Had one of the toughest moments we've had in months after plans changed.`;
const VOICE_MEMO_SAMPLE = `Voice memo: After a busy afternoon she seemed emotional, then calmed down with deep pressure hugs. She ate dinner later and went to bed early.`;

const isNaturalCaptureDebugEnabled = () => {
  return isDebugFlagEnabled(['debugNaturalCaptureDebug', 'debugImportParser'], {
    allowWithoutFlag: true,
  });
};

const isServerTimestampValue = (value) => (
  value
  && typeof value === 'object'
  && !(value instanceof Date)
  && typeof value.toMillis === 'function'
  && typeof value.toDate === 'function'
);

const previewReplacer = (_key, value) => {
  if (value instanceof Date) {
    return value.toISOString();
  }

  if (isServerTimestampValue(value)) {
    return '[Firestore Timestamp]';
  }

  if (typeof value === 'function') {
    return '[Function]';
  }

  return value;
};

const stringifyPreview = (value) => {
  try {
    return JSON.stringify(value, previewReplacer, 2);
  } catch (_error) {
    return '"[Unserializable preview]"';
  }
};

const formatDebugCellValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return '—';
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  if (Array.isArray(value)) {
    if (!value.length) {
      return '[]';
    }
    return value.slice(0, 3).map((item) => {
      if (typeof item === 'string') return item;
      if (item && typeof item === 'object') return item.finalCategory || item.proposedCategory || item.originalCategory || JSON.stringify(item);
      return String(item);
    }).join(' • ');
  }

  if (typeof value === 'object') {
    return value.finalCategory || value.proposedCategory || value.originalCategory || value.reason || value.parserUsed || value.underlyingPath || 'Object';
  }

  return String(value);
};

const summarizeSignals = (signals = []) => {
  if (!Array.isArray(signals) || !signals.length) {
    return '';
  }

  return signals
    .map((signal) => {
      if (typeof signal === 'string') {
        return signal;
      }

      if (signal && typeof signal === 'object') {
        return signal.label || signal.signal || signal.reason || signal.category || signal.text || '';
      }

      return String(signal || '');
    })
    .filter(Boolean)
    .slice(0, 4)
    .join(', ');
};

const explainPromotionReason = (reason = '') => {
  const normalized = String(reason || '').trim().toLowerCase();
  const map = {
    no_proposed_category: 'no stronger category was proposed',
    no_current_category: 'there was no existing category, so the proposed one was used',
    already_matching: 'the parser and the shared category core agreed',
    low_confidence_preserve_current: 'the alternative category was low confidence, so the original category was preserved',
    after_context_non_food: 'it mentions a later-in-the-day context and signal language, not food',
    preserve_behavior_or_mood_over_food: 'signal/mood cues outranked food cues',
    preserve_medication_over_sleep: 'medication cues outranked sleep cues',
    preserve_activity_over_sleep: 'activity cues outranked sleep cues',
    medication_over_sleep: 'medication cues are stronger than sleep cues in this text',
    activity_over_sleep: 'activity cues are stronger than sleep cues in this text',
  };

  return map[normalized] || '';
};

const explainDecision = (row = {}, index = 0, debug = {}) => {
  const decision = row?.metadata?.categoryDecision || debug?.decisionAudit?.[index] || debug?.categoryComparisons?.[index] || {};
  const finalCategory = decision?.finalCategory || row?.category || 'log';
  const originalCategory = decision?.originalCategory || row?.parsedSourceCategory || row?.category || 'unknown';
  const proposedCategory = decision?.proposedCategory || row?.parsedSourceCategory || row?.category || 'unknown';
  const reasonText = explainPromotionReason(decision?.promotionReason);
  const signalText = summarizeSignals(decision?.signals || decision?.currentPathSignals || decision?.reason || []);
  const fallbackText = decision?.fallbackReason ? `Fallback reason: ${decision.fallbackReason}` : '';
  const pieces = [
    reasonText || '',
    signalText ? `Signals: ${signalText}` : '',
    fallbackText,
  ].filter(Boolean);

  if (!pieces.length) {
    pieces.push('No extra promotion was needed; the row kept the parser result.');
  }

  return {
    finalCategory,
    originalCategory,
    proposedCategory,
    promotionApplied: Boolean(decision?.promotionApplied),
    promotionReason: decision?.promotionReason || '',
    summary: `Categorized as ${finalCategory} because ${pieces.join(' ')}.`,
  };
};

const DebugSummaryGrid = ({ debug = {} }) => {
  const primaryDecision = Array.isArray(debug?.decisionAudit) && debug.decisionAudit.length
    ? debug.decisionAudit[0]
    : null;
  const comparisons = Array.isArray(debug?.categoryComparisons) ? debug.categoryComparisons : [];
  const summaryFields = [
    { label: 'Adapter', value: debug.adapterMode || '—' },
    { label: 'Parser', value: debug.parserUsed || '—' },
    { label: 'Shape', value: debug.detectedShape || '—' },
    { label: 'Input type', value: debug.inputType || '—' },
    { label: 'Path', value: debug.underlyingPath || debug.chunkPlanSource || '—' },
    { label: 'Reason', value: debug.reason || primaryDecision?.promotionReason || '—' },
    { label: 'Rows', value: debug.rowCount ?? comparisons.length ?? '—' },
    { label: 'Fallback', value: debug.fallbackReason || primaryDecision?.fallbackReason || '—' },
    { label: 'Final category', value: primaryDecision?.finalCategory || comparisons[0]?.finalCategory || '—' },
    { label: 'Promotion', value: primaryDecision?.promotionApplied !== undefined ? primaryDecision.promotionApplied : (comparisons[0]?.promotionApplied ?? false) },
    { label: 'Promotion reason', value: primaryDecision?.promotionReason || comparisons[0]?.promotionReason || '—' },
  ];

  return (
    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
        Process summary
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, minmax(0, 1fr))',
            md: 'repeat(3, minmax(0, 1fr))',
          },
          gap: 1,
        }}
      >
        {summaryFields.map((field) => (
          <Paper key={field.label} variant="outlined" sx={{ p: 1.25, bgcolor: 'background.paper' }}>
            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontWeight: 700 }}>
              {field.label}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
              {formatDebugCellValue(field.value)}
            </Typography>
          </Paper>
        ))}
      </Box>
      {Array.isArray(comparisons) && comparisons.length ? (
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 2 }}>
          {comparisons.slice(0, 4).map((decision, index) => (
            <Chip
              key={`${decision?.finalCategory || 'row'}-${index}`}
              label={`${decision?.originalCategory || 'unknown'} → ${decision?.finalCategory || 'unknown'}${decision?.promotionApplied ? ` (${decision?.promotionReason || 'promoted'})` : ''}`}
              size="small"
              variant="outlined"
            />
          ))}
        </Stack>
      ) : null}
      <Accordion disableGutters sx={{ mt: 2, borderRadius: 1, overflow: 'hidden' }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Raw debug JSON
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box
            component="pre"
            sx={{
              m: 0,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              fontSize: 12,
              lineHeight: 1.5,
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
            }}
          >
            {stringifyPreview(debug)}
          </Box>
        </AccordionDetails>
      </Accordion>
    </Paper>
  );
};

const RowAuditCard = ({ row = {}, index = 0, debug = {} }) => {
  const explanation = explainDecision(row, index, debug);
  const compactRow = {
    category: row?.category || '',
    title: row?.title || row?.titlePrefix || '',
    body: row?.body || row?.note || row?.text || '',
    timeSource: row?.timeSource || '',
    localTime: row?.localTime || '',
    date: row?.date || '',
    metadata: row?.metadata || {},
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
      <Stack spacing={1.5}>
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Row {index + 1}: {explanation.finalCategory}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {explanation.summary}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Chip size="small" label={`Original: ${explanation.originalCategory}`} variant="outlined" />
          <Chip size="small" label={`Proposed: ${explanation.proposedCategory}`} variant="outlined" />
          <Chip size="small" label={`Final: ${explanation.finalCategory}`} color="primary" variant="outlined" />
          <Chip size="small" label={explanation.promotionApplied ? 'Promoted' : 'Preserved'} variant="outlined" />
        </Box>

        <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'background.paper' }}>
          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontWeight: 700 }}>
            Compact row preview
          </Typography>
          <Box
            component="pre"
            sx={{
              m: 0,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              fontSize: 12,
              lineHeight: 1.5,
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
            }}
          >
            {stringifyPreview(compactRow)}
          </Box>
        </Paper>

        <Accordion disableGutters sx={{ borderRadius: 1, overflow: 'hidden' }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Raw row JSON
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box
              component="pre"
              sx={{
                m: 0,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontSize: 12,
                lineHeight: 1.5,
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
              }}
            >
              {stringifyPreview(row)}
            </Box>
          </AccordionDetails>
        </Accordion>
      </Stack>
    </Paper>
  );
};

const DebugRowsBlock = ({ rows = [], debug = {} }) => (
  <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
      Parsed rows
    </Typography>
    <Stack spacing={2}>
      {Array.isArray(rows) && rows.length ? rows.map((row, index) => (
        <RowAuditCard key={`${row?.title || row?.note || 'row'}-${index}`} row={row} index={index} debug={debug} />
      )) : (
        <Alert severity="info">No rows were returned.</Alert>
      )}
    </Stack>
  </Paper>
);

const buildDebugSavePreview = (rows = [], builder = null, extras = {}) => (
  Array.isArray(rows) ? rows.map((row, index) => {
    const payload = typeof builder === 'function'
      ? builder(row, extras)
      : { row };

    return {
      index,
      inputRow: {
        category: row?.category || '',
        title: row?.title || '',
        titlePrefix: row?.titlePrefix || '',
        note: row?.note || '',
        timeSource: row?.timeSource || '',
        localTime: row?.localTime || '',
        metadata: row?.metadata || {},
      },
      firestorePreview: payload,
    };
  }) : []
);

const DebugOutputBlock = ({ title, value }) => (
  <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
      {title}
    </Typography>
    <Box
      component="pre"
      sx={{
        m: 0,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        fontSize: 12,
        lineHeight: 1.5,
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
      }}
    >
      {stringifyPreview(value)}
    </Box>
  </Paper>
);

const ScenarioPanel = ({
  id,
  label,
  description,
  initialText,
  parseScenario,
  parseButtonLabel = 'Run parse',
  allowFileImport = false,
}) => {
  const [text, setText] = useState(initialText);
  const [selectedFileName, setSelectedFileName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const handleRun = async () => {
    setLoading(true);
    setError('');
    try {
      const nextResult = await parseScenario(text, selectedFileName);
      setResult(nextResult);
    } catch (nextError) {
      setResult(null);
      setError(nextError?.message || 'Unable to parse the sample.');
    } finally {
      setLoading(false);
    }
  };

  const handleLoadFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setLoading(true);
    setError('');
    try {
      const extractedText = await extractTextFromImportFile(file);
      setSelectedFileName(file.name || '');
      setText(extractedText);
    } catch (nextError) {
      setError(nextError?.message || 'Unable to read that file.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Accordion defaultExpanded={id === 'quick-simple'} disableGutters sx={{ borderRadius: 2, overflow: 'hidden' }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {label}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={2}>
          {allowFileImport && (
            <Button component="label" variant="outlined" size="small" sx={{ alignSelf: 'flex-start' }}>
              Load file text
              <input
                hidden
                type="file"
                accept=".pdf,.txt,.docx,.xlsx,application/pdf,text/plain"
                onChange={handleLoadFile}
              />
            </Button>
          )}
          {selectedFileName ? (
            <Alert severity="info">Loaded file: {selectedFileName}</Alert>
          ) : null}
          <TextField
            label="Input text"
            multiline
            minRows={6}
            fullWidth
            value={text}
            onChange={(event) => setText(event.target.value)}
          />
          <Stack direction="row" spacing={1} alignItems="center">
            <Button variant="contained" onClick={handleRun} disabled={loading}>
              {loading ? 'Running...' : parseButtonLabel}
            </Button>
            <Button variant="text" component={RouterLink} to="/admin">
              Back to Admin
            </Button>
          </Stack>
          {error ? <Alert severity="error">{error}</Alert> : null}
          {result ? (
            <Stack spacing={2}>
              <DebugOutputBlock title="Original text" value={result.originalText} />
              <DebugSummaryGrid debug={result.debug} />
              <DebugRowsBlock rows={result.rows} debug={result.debug} />
              <DebugOutputBlock title="Firestore save preview" value={result.savePreview} />
            </Stack>
          ) : null}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

const NaturalCaptureDebugPage = () => {
  const isDevDebugEnabled = isNaturalCaptureDebugEnabled();

  const quickSimpleParser = async (text) => {
    const captureState = getQuickNoteCaptureState(text);
    const resolvedCategory = captureState.category || 'log';
    const rows = (captureState.entities || []).map((entity, index) => ({
      ...entity,
      index,
      sourceText: String(text || '').trim(),
      originalText: String(text || '').trim(),
      parsedObservation: entity?.category === 'sleep' || entity?.category === 'mood',
      parsedSourceCategory: entity?.category || resolvedCategory,
    }));

    return {
      originalText: String(text || '').trim(),
      debug: {
        adapterMode: 'quick_simple_entry',
        parserUsed: 'getQuickNoteCaptureState',
        inputType: 'quick_log',
        captureState,
        rowCount: rows.length,
      },
      rows,
      savePreview: [
        buildCanonicalQuickNoteSavePayload({
          child: { id: 'debug-child' },
          text,
          selectedDate: new Date(),
          selectedTime: '',
          resolvedCategory,
          noteData: { title: captureState.labels?.[0] || 'Quick note' },
          metadata: {
            source: 'natural-capture-debug',
            scenario: 'quick_simple_entry',
          },
          parserMode: 'quick_log',
          sourceMode: 'quick_log',
          inputMode: 'quick_log',
        }),
      ],
    };
  };

  const structuredQuickParser = async (text) => {
    const parsed = parseNaturalCaptureText(text, {
      adapterMode: 'quick_log_structured',
      parserMode: 'quick_log_structured',
      sourceMode: 'quick_log_structured',
      inputMode: 'quick_log_structured',
      rawText: text,
      defaultDate: new Date(),
    });
    return {
      originalText: String(text || '').trim(),
      debug: parsed?.debug || {},
      rows: parsed?.rows || [],
      savePreview: buildDebugSavePreview(parsed?.rows || [], (row) => buildCanonicalStructuredQuickNoteSaveEntry({
        entity: row,
        child: { id: 'debug-child' },
        noteText: text,
        selectedDate: new Date(),
        selectedTime: '',
        importantMoment: false,
        user: { uid: 'debug-user', displayName: 'Debug User', email: 'debug@example.com' },
        isBackendSmartCapture: false,
        allowSelectedTimeForSingleEntity: true,
        index: 0,
        timeZoneName: Intl.DateTimeFormat().resolvedOptions().timeZone || null,
        timeZoneOffsetMinutes: -new Date().getTimezoneOffset(),
        combinedTags: [],
      })),
    };
  };

  const longNoteParser = async (text) => {
    const parsed = parseNaturalCaptureText(text, {
      adapterMode: 'long_note_email',
      parserMode: 'long_note_email',
      sourceMode: 'long_note_email',
      inputMode: 'import',
      rawText: text,
      defaultDate: new Date(),
    });
    const rows = parsed?.rows || [];
    return {
      originalText: String(text || '').trim(),
      debug: parsed?.debug || {},
      rows,
      savePreview: buildDebugSavePreview(rows, (row) => buildPreservedImportDocData(row, {
        childId: 'debug-child',
        authorName: 'Debug User',
        authorEmail: 'debug@example.com',
        source: { type: 'debug', mode: 'debug' },
      })),
    };
  };

  const importedFileParser = async (text) => {
    const parsed = await parseImportedFileText(text, {
      defaultDate: new Date(),
      childName: 'Debug Child',
    });
    const rows = parsed?.entries || [];
    return {
      originalText: String(text || '').trim(),
      debug: parsed?.importDebug || {},
      rows,
      savePreview: buildDebugSavePreview(rows, (row) => buildPreservedImportDocData(row, {
        childId: 'debug-child',
        authorName: 'Debug User',
        authorEmail: 'debug@example.com',
        source: { type: 'debug', mode: 'debug' },
      })),
    };
  };

  const voiceMemoParser = async (text) => {
    const parsed = parseNaturalCaptureText(text, {
      adapterMode: 'long_note_email',
      parserMode: 'voice_memo',
      sourceMode: 'voice_memo',
      inputMode: 'voice_memo',
      rawText: text,
      defaultDate: new Date(),
    });
    const rows = parsed?.rows || [];
    return {
      originalText: String(text || '').trim(),
      debug: parsed?.debug || {},
      rows,
      savePreview: buildDebugSavePreview(rows, (row) => buildPreservedImportDocData(row, {
        childId: 'debug-child',
        authorName: 'Debug User',
        authorEmail: 'debug@example.com',
        source: { type: 'debug', mode: 'debug' },
      })),
    };
  };

  const scenarios = [
    {
      id: 'quick-simple',
      label: 'Quick simple entry',
      description: 'Uses the quick note capture state and shows the quick-note save preview.',
      initialText: QUICK_SIMPLE_SAMPLE,
      parseScenario: quickSimpleParser,
      parseButtonLabel: 'Run quick-note capture',
      allowFileImport: false,
    },
    {
      id: 'quick-structured',
      label: 'Slightly longer structured entry',
      description: 'Runs the structured quick-log natural capture adapter.',
      initialText: QUICK_STRUCTURED_SAMPLE,
      parseScenario: structuredQuickParser,
      parseButtonLabel: 'Run structured parse',
      allowFileImport: false,
    },
    {
      id: 'pasted-email',
      label: 'Pasted email',
      description: 'Runs the long-note/email adapter and shows import-save preview rows.',
      initialText: PASTED_EMAIL_SAMPLE,
      parseScenario: longNoteParser,
      parseButtonLabel: 'Run email parse',
      allowFileImport: false,
    },
    {
      id: 'imported-file',
      label: 'Imported file text',
      description: 'Load extracted DOCX/XLSX text, then preview the imported-file parser path.',
      initialText: IMPORTED_FILE_SAMPLE,
      parseScenario: importedFileParser,
      parseButtonLabel: 'Run imported-file parse',
      allowFileImport: true,
    },
    {
      id: 'voice-memo',
      label: 'Voice typed memo',
      description: 'Treats a dictated memo like a long-form capture source and previews the same canonical rows.',
      initialText: VOICE_MEMO_SAMPLE,
      parseScenario: voiceMemoParser,
      parseButtonLabel: 'Run voice memo parse',
      allowFileImport: false,
    },
  ];

  if (!isDevDebugEnabled) {
    return (
      <ResponsiveLayout pageTitle="Natural Capture Debug" showBottomNav={false}>
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Alert severity="error">
            Natural capture debug is only available on localhost or the test deploy.
          </Alert>
        </Container>
      </ResponsiveLayout>
    );
  }

  return (
    <ResponsiveLayout pageTitle="Natural Capture Debug" showBottomNav={false}>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 800 }}>
            Natural Capture Debug
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Paste text, run a parser adapter, and inspect the original text, decision metadata, parsed rows,
            and the Firestore save preview. Nothing is saved from this page.
          </Typography>
        </Paper>

        <Stack spacing={2}>
          {scenarios.map((scenario) => (
            <ScenarioPanel
              key={scenario.id}
              id={scenario.id}
              label={scenario.label}
              description={scenario.description}
              initialText={scenario.initialText}
              parseScenario={scenario.parseScenario}
              parseButtonLabel={scenario.parseButtonLabel}
              allowFileImport={scenario.allowFileImport}
            />
          ))}
        </Stack>

        <Divider sx={{ my: 4 }} />

        <Button component={RouterLink} to="/admin" variant="outlined">
          Back to Admin Tools
        </Button>
      </Container>
    </ResponsiveLayout>
  );
};

export default NaturalCaptureDebugPage;
